import React, { useEffect, useRef, useState } from 'react';
import './Categories.css';
import { useLanguage } from '../../context/LanguageContext';

// Import dynamique des images de catégories
const importAll = (r) => {
  let images = {};
  r.keys().forEach((item) => {
    const name = item.replace('./', '').replace(/\.\w+$/, '');
    images[name] = r(item);
  });
  return images;
};

// Fonction utilitaire pour formater les noms de catégories
const formatCategoryName = (name) => {
  return name
    .replace(/([A-Z])/g, ' $1') // Ajoute un espace avant chaque majuscule
    .replace(/^./, (str) => str.toUpperCase()); // Met en majuscule la première lettre
};

// Fonction pour obtenir le nom standardisé de la catégorie pour la traduction
const getStandardCategoryName = (originalName) => {
  // Convertit 'categoryName' en 'Category Name' pour correspondre aux clés dans le contexte de langue
  const formatted = originalName
    .replace(/([a-z])([A-Z])/g, '$1 $2') // Insère des espaces entre camelCase
    .split(/[\s_-]+/) // Divise sur les espaces, underscores ou tirets
    .map(word => word.charAt(0).toUpperCase() + word.slice(1)) // Majuscule pour chaque mot
    .join(' ');
    
  return formatted;
};

// Base de données de fun facts pour les catégories
const getFunFactForCategory = (categoryName, currentLanguage = 'fr') => {
  const funFacts = {
    fr: {
      'Africa Cultural': "L'Afrique compte plus de 3 000 groupes ethniques différents et plus de 2 000 langues.",
      'Afrobeats Modern': "L'Afrobeats est un mélange de sons africains traditionnels avec des éléments de hip-hop, R&B et dancehall.",
      'America Latina': "L'Amérique latine abrite la forêt amazonienne, qui produit 20% de l'oxygène de la planète.",
      'Anatomie': "Le corps humain contient environ 100 000 kilomètres de vaisseaux sanguins.",
      'Animal Crossing': "Dans Animal Crossing, chaque objet possède un numéro unique dans le code du jeu.",
      'Animals': "Le cœur d'une baleine bleue est si grand qu'un humain pourrait nager dans ses artères.",
      'Antiquity': "Les Romains utilisaient de l'urine comme détergent et produit blanchissant pour les dents.",
      'Aot': "Attack on Titan a été créé après que son auteur, Hajime Isayama, ait rencontré un client ivre dans un café où il travaillait.",
      'Archipel Island': "L'archipel d'Hawaï se déplace vers le nord-ouest à la vitesse de 7,5 cm par an.",
      'Arsenal Fc': "Arsenal est le seul club de Premier League à avoir terminé une saison entière sans défaite (2003-2004).",
      'Artistic Movement': "Le courant impressionniste a été nommé d'après un tableau de Claude Monet, 'Impression, soleil levant'.",
      'Asiatic Cultural': "Le Japon compte plus de 6 800 îles, dont seulement 430 sont habitées.",
      'Assassins Creed': "La série Assassin's Creed est inspirée du roman 'Alamut' de Vladimir Bartol, publié en 1938.",
      'Astronomie': "Une journée sur Vénus est plus longue qu'une année vénusienne - il faut 243 jours terrestres pour que Vénus tourne sur elle-même.",
      'Athletism': "Le record du monde du 100 mètres détenu par Usain Bolt (9,58 secondes) correspond à une vitesse de 37,58 km/h.",
      'Attentats And Crises': "Après les attentats du 11 septembre 2001, les avions ont été interdits de vol aux États-Unis pendant 3 jours.",
      'Batman': "Le personnage de Batman a été créé en 1939, inspiré par Zorro, Sherlock Holmes et un dessin de Léonard de Vinci.",
      'Big Realisator': "Alfred Hitchcock apparaît brièvement dans 39 de ses 52 films, créant ainsi sa propre marque de fabrique.",
      'Paris Saint Germain': "Le PSG est le club le plus titré de l'histoire du football français avec plus de 40 trophées nationaux.",
      'Peaky Blinders': "La série Peaky Blinders est inspirée d'un véritable gang de Birmingham qui cousait des lames de rasoir dans leurs casquettes.",
      'Peintres': "Vincent van Gogh n'a vendu qu'un seul tableau de son vivant, 'La Vigne Rouge'.",
      'Peter Pan': "J.M. Barrie a légué tous les droits de Peter Pan à l'hôpital pour enfants Great Ormond Street de Londres.",
      'Plants': "Les plantes peuvent communiquer entre elles à travers un réseau de champignons souterrains surnommé le 'Wood Wide Web'.",
      'Poetes': "La poétesse Emily Dickinson n'a publié que 10 poèmes de son vivant, sur plus de 1 800 écrits.",
      'Pop': "Le terme 'pop music' est apparu pour la première fois dans un journal britannique en 1926.",
      'Power': "La série Power a été produite par Curtis '50 Cent' Jackson, qui joue également le rôle de Kanan Stark.",
      'Pre History': "Les premiers outils en pierre ont été fabriqués il y a environ 2,6 millions d'années, bien avant l'apparition d'Homo sapiens.",
      'Premier League': "La Premier League anglaise est diffusée dans 212 territoires à travers le monde et touche environ 4,7 milliards de personnes.",
      'Prison Break': "La prison d'État de Joliet, où la première saison a été filmée, a fermé ses portes en 2002.",
      'Programmation': "La première programmeuse de l'histoire était une femme, Ada Lovelace, qui a écrit le premier algorithme en 1843.",
      'Psycho Pass': "Dans Psycho-Pass, le système Sibyl s'inspire du concept de 'précrime' présent dans la nouvelle de Philip K. Dick 'Minority Report'.",
      'Rap Fr Modern': "Le rap français est le deuxième marché mondial du rap après les États-Unis.",
      'Rap Us Modern': "En 2017, le hip-hop/rap est devenu le genre musical le plus écouté aux États-Unis, dépassant le rock pour la première fois.",
      'Reggae': "Le reggae a été inscrit au patrimoine culturel immatériel de l'UNESCO en 2018.",
      'Retour Vers Le Futur': "La DeLorean de 'Retour vers le futur' ne pouvait pas dépasser 88 mph dans la réalité à cause de son moteur peu puissant.",
      'Revolution And Empire': "Napoléon Bonaparte mesurait en réalité 1m68, une taille moyenne pour son époque, contrairement au mythe du 'petit caporal'.",
      'Rnb': "Le terme 'Rhythm and Blues' a été créé en 1947 par Jerry Wexler, journaliste pour Billboard.",
      'Rock': "Le premier disque considéré comme du rock and roll était 'Rocket 88' de Jackie Brenston and his Delta Cats, sorti en 1951.",
      'Rolland Garros': "Roland Garros est le seul tournoi du Grand Chelem qui se joue sur terre battue.",
      'Rugby': "Le ballon de rugby a une forme ovale car à l'origine, il était fabriqué avec une vessie de porc.",
      'Savoir Faire Traditional': "La tapisserie de Bayeux, chef-d'œuvre médiéval de 70 mètres de long, a été créée au 11ème siècle et raconte la conquête de l'Angleterre.",
      'Scientific Discovery': "Albert Einstein n'a jamais échoué en mathématiques à l'école, contrairement à la légende urbaine.",
      'Series': "La série la plus longue de l'histoire est 'Guiding Light', qui a duré 72 ans (radio puis télévision)."
    },
    en: {
      'Africa Cultural': "Africa has over 3,000 different ethnic groups and more than 2,000 languages.",
      'Afrobeats Modern': "Afrobeats is a blend of traditional African sounds with elements of hip-hop, R&B, and dancehall.",
      'America Latina': "Latin America is home to the Amazon rainforest, which produces 20% of the planet's oxygen.",
      'Anatomie': "The human body contains approximately 60,000 miles of blood vessels.",
      'Animal Crossing': "In Animal Crossing, each object has a unique number in the game code.",
      'Animals': "A blue whale's heart is so big that a human could swim through its arteries.",
      'Antiquity': "Romans used urine as detergent and teeth whitener.",
      'Aot': "Attack on Titan was created after its author, Hajime Isayama, met a drunk customer at a café where he worked.",
      'Archipel Island': "The Hawaiian archipelago moves northwest at a rate of 3 inches per year.",
      'Arsenal Fc': "Arsenal is the only Premier League club to have completed an entire season undefeated (2003-2004).",
      'Artistic Movement': "The Impressionist movement was named after Claude Monet's painting, 'Impression, Sunrise'.",
      'Asiatic Cultural': "Japan consists of over 6,800 islands, but only 430 are inhabited.",
      'Assassins Creed': "The Assassin's Creed series was inspired by Vladimir Bartol's 1938 novel 'Alamut'.",
      'Astronomie': "A day on Venus is longer than a Venusian year - it takes 243 Earth days for Venus to rotate once on its axis.",
      'Athletism': "Usain Bolt's world record in the 100 meters (9.58 seconds) equates to a speed of 23.35 mph.",
      'Attentats And Crises': "After the 9/11 attacks, airplanes were banned from flying in the US for 3 days.",
      'Batman': "Batman was created in 1939, inspired by Zorro, Sherlock Holmes, and a drawing by Leonardo da Vinci.",
      'Big Realisator': "Alfred Hitchcock makes brief appearances in 39 of his 52 films, creating his signature trademark.",
      'Paris Saint Germain': "PSG is the most decorated club in French football history with over 40 domestic trophies.",
      'Peaky Blinders': "Peaky Blinders is based on a real Birmingham gang that sewed razor blades into their caps.",
      'Peintres': "Vincent van Gogh only sold one painting during his lifetime, 'The Red Vineyard'.",
      'Peter Pan': "J.M. Barrie donated all rights to Peter Pan to the Great Ormond Street Hospital for Children in London.",
      'Plants': "Plants can communicate with each other through an underground network of fungi nicknamed the 'Wood Wide Web'.",
      'Poetes': "Poet Emily Dickinson published only 10 poems during her lifetime, out of more than 1,800 written.",
      'Pop': "The term 'pop music' first appeared in a British newspaper in 1926.",
      'Power': "The series Power was produced by Curtis '50 Cent' Jackson, who also plays the role of Kanan Stark.",
      'Pre History': "The first stone tools were made about 2.6 million years ago, long before Homo sapiens appeared.",
      'Premier League': "The English Premier League is broadcast in 212 territories worldwide, reaching approximately 4.7 billion people.",
      'Prison Break': "Joliet State Prison, where the first season was filmed, closed in 2002.",
      'Programmation': "The first programmer in history was a woman, Ada Lovelace, who wrote the first algorithm in 1843.",
      'Psycho Pass': "In Psycho-Pass, the Sibyl System is inspired by the concept of 'precrime' from Philip K. Dick's short story 'Minority Report'.",
      'Rap Fr Modern': "French rap is the second largest rap market in the world after the United States.",
      'Rap Us Modern': "In 2017, hip-hop/rap became the most listened to music genre in the US, surpassing rock for the first time.",
      'Reggae': "Reggae was inscribed on UNESCO's Intangible Cultural Heritage list in 2018.",
      'Retour Vers Le Futur': "The DeLorean from 'Back to the Future' couldn't actually go over 88 mph in real life due to its underpowered engine.",
      'Revolution And Empire': "Napoleon Bonaparte was actually 5'7\" tall, average height for his era, contrary to the 'little corporal' myth.",
      'Rnb': "The term 'Rhythm and Blues' was created in 1947 by Jerry Wexler, a journalist for Billboard.",
      'Rock': "The first record considered to be rock and roll was 'Rocket 88' by Jackie Brenston and his Delta Cats, released in 1951.",
      'Rolland Garros': "Roland Garros is the only Grand Slam tournament played on clay courts.",
      'Rugby': "The rugby ball has an oval shape because it was originally made using a pig's bladder.",
      'Savoir Faire Traditional': "The Bayeux Tapestry, a medieval masterpiece 70 meters long, was created in the 11th century and tells the story of the conquest of England.",
      'Scientific Discovery': "Albert Einstein never failed math in school, contrary to the urban legend.",
      'Series': "The longest-running series in history is 'Guiding Light', which lasted 72 years (radio then television)."
    },
    es: {
      'Africa Cultural': "África tiene más de 3.000 grupos étnicos diferentes y más de 2.000 idiomas.",
      'Afrobeats Modern': "Afrobeats es una mezcla de sonidos africanos tradicionales con elementos de hip-hop, R&B y dancehall.",
      'America Latina': "América Latina alberga la selva amazónica, que produce el 20% del oxígeno del planeta.",
      'Anatomie': "El cuerpo humano contiene aproximadamente 100.000 kilómetros de vasos sanguíneos.",
      'Animal Crossing': "En Animal Crossing, cada objeto tiene un número único en el código del juego.",
      'Animals': "El corazón de una ballena azul es tan grande que un humano podría nadar a través de sus arterias.",
      'Antiquity': "Los romanos usaban orina como detergente y blanqueador de dientes.",
      'Aot': "Attack on Titan fue creado después de que su autor, Hajime Isayama, conociera a un cliente borracho en un café donde trabajaba.",
      'Archipel Island': "El archipiélago de Hawái se mueve hacia el noroeste a una velocidad de 7,5 cm por año.",
      'Arsenal Fc': "Arsenal es el único club de la Premier League que ha completado una temporada entera sin derrotas (2003-2004).",
      'Artistic Movement': "El movimiento impresionista fue nombrado a partir de un cuadro de Claude Monet, 'Impresión, sol naciente'.",
      'Asiatic Cultural': "Japón consta de más de 6.800 islas, pero solo 430 están habitadas.",
      'Assassins Creed': "La serie Assassin's Creed está inspirada en la novela 'Alamut' de Vladimir Bartol, publicada en 1938.",
      'Astronomie': "Un día en Venus es más largo que un año venusiano: tarda 243 días terrestres en girar sobre sí mismo.",
      'Athletism': "El récord mundial de los 100 metros de Usain Bolt (9,58 segundos) equivale a una velocidad de 37,58 km/h.",
      'Attentats And Crises': "Tras los atentados del 11 de septiembre de 2001, se prohibió volar a los aviones en Estados Unidos durante 3 días.",
      'Batman': "El personaje de Batman fue creado en 1939, inspirado en Zorro, Sherlock Holmes y un dibujo de Leonardo da Vinci.",
      'Big Realisator': "Alfred Hitchcock aparece brevemente en 39 de sus 52 películas, creando así su propia marca registrada.",
      'Paris Saint Germain': "El PSG es el club más laureado de la historia del fútbol francés con más de 40 trofeos nacionales.",
      'Peaky Blinders': "Peaky Blinders está basada en una pandilla real de Birmingham que cosía cuchillas de afeitar en sus gorras.",
      'Peintres': "Vincent van Gogh solo vendió un cuadro durante su vida, 'La Viña Roja'.",
      'Peter Pan': "J.M. Barrie donó todos los derechos de Peter Pan al Hospital para Niños Great Ormond Street de Londres.",
      'Plants': "Las plantas pueden comunicarse entre sí a través de una red subterránea de hongos apodada la 'Wood Wide Web'.",
      'Poetes': "La poetisa Emily Dickinson publicó solo 10 poemas durante su vida, de más de 1.800 escritos.",
      'Pop': "El término 'música pop' apareció por primera vez en un periódico británico en 1926.",
      'Power': "La serie Power fue producida por Curtis '50 Cent' Jackson, quien también interpreta el papel de Kanan Stark.",
      'Pre History': "Las primeras herramientas de piedra fueron fabricadas hace unos 2,6 millones de años, mucho antes de la aparición del Homo sapiens.",
      'Premier League': "La Premier League inglesa se transmite en 212 territorios en todo el mundo y llega a aproximadamente 4,7 mil millones de personas.",
      'Prison Break': "La prisión estatal de Joliet, donde se filmó la primera temporada, cerró en 2002.",
      'Programmation': "La primera programadora de la historia fue una mujer, Ada Lovelace, quien escribió el primer algoritmo en 1843.",
      'Psycho Pass': "En Psycho-Pass, el sistema Sibyl se inspira en el concepto de 'precrimen' presente en el relato de Philip K. Dick 'Minority Report'.",
      'Rap Fr Modern': "El rap francés es el segundo mercado mundial del rap después de Estados Unidos.",
      'Rap Us Modern': "En 2017, el hip-hop/rap se convirtió en el género musical más escuchado en Estados Unidos, superando al rock por primera vez.",
      'Reggae': "El reggae fue inscrito en el patrimonio cultural inmaterial de la UNESCO en 2018.",
      'Retour Vers Le Futur': "El DeLorean de 'Regreso al Futuro' no podía superar las 88 mph en la realidad debido a su motor poco potente.",
      'Revolution And Empire': "Napoleón Bonaparte medía en realidad 1,68 m, una altura media para su época, contrariamente al mito del 'pequeño cabo'.",
      'Rnb': "El término 'Rhythm and Blues' fue creado en 1947 por Jerry Wexler, periodista de Billboard.",
      'Rock': "El primer disco considerado como rock and roll fue 'Rocket 88' de Jackie Brenston and his Delta Cats, lanzado en 1951.",
      'Rolland Garros': "Roland Garros es el único torneo de Grand Slam que se juega en tierra batida.",
      'Rugby': "El balón de rugby tiene forma ovalada porque originalmente estaba hecho con una vejiga de cerdo.",
      'Savoir Faire Traditional': "El tapiz de Bayeux, obra maestra medieval de 70 metros de largo, fue creado en el siglo XI y cuenta la historia de la conquista de Inglaterra.",
      'Scientific Discovery': "Albert Einstein nunca reprobó matemáticas en la escuela, contrario a la leyenda urbana.",
      'Series': "La serie más larga de la historia es 'Guiding Light', que duró 72 años (radio y luego televisión)."
    }
  };

  // Chercher le fun fact dans la langue courante, ou utiliser le français par défaut
  const factInLanguage = funFacts[currentLanguage]?.[categoryName] || funFacts.fr[categoryName];
  
  // Si la catégorie n'est pas trouvée, retourner un fait générique dans la langue courante
  if (!factInLanguage) {
    const genericFacts = {
      fr: "Savais-tu que cette catégorie contient des dizaines de questions passionnantes ?",
      en: "Did you know that this category contains dozens of exciting questions?",
      es: "¿Sabías que esta categoría contiene docenas de preguntas emocionantes?"
    };
    return genericFacts[currentLanguage] || genericFacts.fr;
  }
  
  return factInLanguage;
};

// Composant pour les particules
const CategoriesParticles = () => {
  const canvasRef = useRef(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Ajuster la taille du canvas à la section
    const resizeCanvas = () => {
      const parentElement = canvas.parentElement;
      if (parentElement) {
        canvas.width = parentElement.offsetWidth;
        canvas.height = parentElement.offsetHeight;
      }
    };
    
    window.addEventListener('resize', resizeCanvas);
    resizeCanvas();
    
    // Configuration des particules
    const particlesArray = [];
    const numberOfParticles = Math.min(window.innerWidth / 8, 150); // Même nombre de particules que Hero
    
    const mouse = {
      x: undefined,
      y: undefined,
      radius: 100 // Même rayon d'interaction que Hero
    };
    
    // Capturer la position de la souris
    const handleMouseMove = (event) => {
      const rect = canvas.getBoundingClientRect();
      mouse.x = event.clientX - rect.left;
      mouse.y = event.clientY - rect.top;
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    
    class Particle {
      constructor() {
        // Position sur tout l'écran sans restriction
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        
        // Taille variable pour plus de dynamisme - identique à Hero
        this.size = Math.random() * 2.5 + 0.8;
        this.baseSize = this.size;
        
        // Vitesse et direction - identique à Hero
        this.speedX = (Math.random() * 1 - 0.5) * 0.6;
        this.speedY = (Math.random() * 1 - 0.5) * 0.6;
        this.baseSpeedX = this.speedX;
        this.baseSpeedY = this.speedY;
        
        // Animation et apparence - identique à Hero
        this.pulsing = Math.random() * 0.03 + 0.01;
        this.colorAngle = Math.random() * 360;
        this.colorSpeed = Math.random() * 0.3 + 0.1;
        this.alpha = Math.random() * 0.4 + 0.3;
        this.glow = Math.random() * 6 + 2;
        this.hueOffset = Math.random() * 20 - 10;
      }
      
      update() {
        // Mouvement de base
        this.x += this.speedX;
        this.y += this.speedY;
        
        // Animation pulsante et changement de couleur
        this.colorAngle += this.colorSpeed;
        if (this.colorAngle > 360) this.colorAngle = 0;
        
        // Effet respirant
        this.size = this.baseSize + Math.sin(Date.now() * this.pulsing) * (this.baseSize * 0.3);
        
        // Interaction avec la souris
        if (mouse.x) {
          const mdx = mouse.x - this.x;
          const mdy = mouse.y - this.y;
          const mouseDistance = Math.sqrt(mdx * mdx + mdy * mdy);
          
          if (mouseDistance < mouse.radius) {
            const angle = Math.atan2(mdy, mdx);
            const force = (mouse.radius - mouseDistance) / mouse.radius;
            
            // Effet de répulsion
            this.speedX -= Math.cos(angle) * force * 0.2;
            this.speedY -= Math.sin(angle) * force * 0.2;
            
            // Augmenter l'opacité et la taille à l'approche de la souris
            this.alpha = Math.min(this.alpha + 0.2, 0.9);
            this.size = this.baseSize * (1 + force * 0.9);
          } else {
            // Revenir progressivement à la vitesse et opacité de base
            this.speedX = this.speedX * 0.97 + this.baseSpeedX * 0.03;
            this.speedY = this.speedY * 0.97 + this.baseSpeedY * 0.03;
            this.alpha = Math.max(this.alpha * 0.98, Math.random() * 0.4 + 0.3);
            this.size = Math.max(this.size * 0.98, this.baseSize);
          }
        }
        
        // Limiter la vitesse
        const maxSpeed = 1.5;
        const currentSpeed = Math.sqrt(this.speedX * this.speedX + this.speedY * this.speedY);
        if (currentSpeed > maxSpeed) {
          this.speedX = (this.speedX / currentSpeed) * maxSpeed;
          this.speedY = (this.speedY / currentSpeed) * maxSpeed;
        }
        
        // Friction légère
        this.speedX *= 0.99;
        this.speedY *= 0.99;
        
        // Rebond aux limites de l'écran
        const margin = 10; // Marge pour éviter que les particules ne disparaissent complètement
        
        if (this.x < margin) {
          this.x = margin;
          this.speedX *= -0.8;
        } else if (this.x > canvas.width - margin) {
          this.x = canvas.width - margin;
          this.speedX *= -0.8;
        }
        
        if (this.y < margin) {
          this.y = margin;
          this.speedY *= -0.8;
        } else if (this.y > canvas.height - margin) {
          this.y = canvas.height - margin;
          this.speedY *= -0.8;
        }
      }
      
      draw() {
        // Centre plus défini avec moins de halo - identique à Hero
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size * 0.7, 0, Math.PI * 2);
        
        // Couleur magenta avec variation
        const hue = Math.abs(Math.sin(this.colorAngle) * 30) + 290 + this.hueOffset;
        const saturation = 100;
        const lightness = 68 + Math.sin(this.colorAngle * 0.5) * 8;
        
        // Centre plus opaque pour mieux définir la particule
        ctx.fillStyle = `hsla(${hue}, ${saturation}%, ${lightness}%, ${this.alpha * 1.2})`;
        ctx.fill();
        
        // Effet de halo lumineux
        const gradient = ctx.createRadialGradient(
          this.x, this.y, 0,
          this.x, this.y, this.size + this.glow
        );
        
        gradient.addColorStop(0, `hsla(${hue}, ${saturation}%, ${lightness + 10}%, ${this.alpha * 0.9})`);
        gradient.addColorStop(0.3, `hsla(${hue}, ${saturation}%, ${lightness}%, ${this.alpha * 0.6})`);
        gradient.addColorStop(0.6, `hsla(${hue}, ${saturation}%, ${lightness - 10}%, ${this.alpha * 0.2})`);
        gradient.addColorStop(1, `hsla(${hue}, ${saturation}%, ${lightness - 20}%, 0)`);
        
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size + this.glow, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    
    // Créer les particules
    const init = () => {
      particlesArray.length = 0;
      for (let i = 0; i < numberOfParticles; i++) {
        particlesArray.push(new Particle());
      }
    };
    init();
    
    // Animer les particules
    let animationFrameId;
    function animate() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      particlesArray.forEach(particle => {
        particle.update();
        particle.draw();
      });
      
      animationFrameId = requestAnimationFrame(animate);
    }
    animate();
    
    // Nettoyage
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      window.removeEventListener('mousemove', handleMouseMove);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);
  
  return (
    <canvas ref={canvasRef} className="categories-particles" />
  );
};

// Composant pour chaque catégorie
const CategoryItem = ({ category, index, isActive, onHover, offHover }) => {
  const { t, currentLanguage } = useLanguage();
  
  // Styles pour l'animation et l'apparence
  const itemStyle = {
    animation: `fadeIn 0.6s forwards ${index % 5 * 0.1}s`,
    opacity: 0,
    transform: isActive ? 'scale(1.08) rotate(0deg)' : 'translateY(20px) rotate(-8deg)',
    zIndex: isActive ? 30 : 'auto', // Augmenté pour s'assurer que tout l'élément est au-dessus des autres
    transition: 'all 0.3s ease'
  };

  const funFactStyle = {
    opacity: isActive ? 1 : 0,
    bottom: '-85px',
    left: '50%',
    transform: isActive ? 'translateX(-50%)' : 'translateX(-50%) translateY(10px)',
    width: '250px',
    zIndex: 200,
    pointerEvents: 'none',
    transition: 'opacity 0.3s, transform 0.3s',
    position: 'absolute'
  };

  // Obtenir le nom standardisé pour la traduction
  const standardName = getStandardCategoryName(category.originalName || category.name);
  
  // Utiliser la traduction si disponible, sinon afficher le nom formaté
  const categoryName = t(standardName) || category.name;

  return (
    <div 
      className="category-item" 
      style={itemStyle}
      onMouseEnter={() => onHover(index)}
      onMouseLeave={() => offHover()}
    >
      <div className="category-image-container">
        <img 
          src={category.src} 
          alt={categoryName} 
          className="category-image" 
        />
      </div>
      <span className="category-name">{categoryName}</span>
      
      {isActive && (
        <div className="fun-fact-container" style={funFactStyle}>
          <div className="fun-fact-content">
            <span className="fun-fact-icon">💡</span>
            <p>{getFunFactForCategory(standardName, currentLanguage)}</p>
          </div>
        </div>
      )}
    </div>
  );
};

function Categories() {
  const { t } = useLanguage();
  const [categoryImages, setCategoryImages] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const containerRef = useRef(null);
  const carouselRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);
  const [activeCategoryIndex, setActiveCategoryIndex] = useState(null);
  const [counterVisible, setCounterVisible] = useState(false);
  const [counterValue, setCounterValue] = useState(0);
  // Ref pour stocker la position de défilement actuelle
  const scrollPosRef = useRef(0);
  const speedRef = useRef(1.5);

  // Animation du compteur
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setTimeout(() => {
            setCounterVisible(true);
            // Lancer l'animation de défilement
            let count = 0;
            const interval = setInterval(() => {
              count += 5;
              setCounterValue(count);
              if (count >= 100) {
                clearInterval(interval);
              }
            }, 50); // Défiler rapidement
          }, 300);
        }
      },
      { threshold: 0.5 }
    );

    const titleElement = document.querySelector('.section-title');
    if (titleElement) {
      observer.observe(titleElement);
    }

    return () => {
      if (titleElement) {
        observer.unobserve(titleElement);
      }
    };
  }, []);

  // Charger les images de catégories
  useEffect(() => {
    // Dans un environnement de production, nous utiliserions une approche comme celle-ci:
    try {
      const categoryImagesContext = require.context('../../assets/categoriesImage', false, /\.(png|jpe?g)$/);
      const images = importAll(categoryImagesContext);
      const imageArray = Object.keys(images).map(key => ({
        name: formatCategoryName(key),
        src: images[key],
        originalName: key
      }));
      
      // Filtrer les catégories dupliquées (quizzerverse et attackontitan)
      const filteredArray = imageArray.filter(category => 
        category.originalName !== 'quizzerverse' && 
        (category.originalName === 'aot' || category.originalName !== 'attackontitan')
      );
      
      // Triez les catégories par ordre alphabétique selon la langue actuelle
      filteredArray.sort((a, b) => {
        const nameA = t(getStandardCategoryName(a.originalName)) || a.name;
        const nameB = t(getStandardCategoryName(b.originalName)) || b.name;
        return nameA.localeCompare(nameB);
      });
      
      setCategoryImages(filteredArray);
      setLoaded(true);
    } catch (error) {
      console.error("Erreur lors du chargement des images de catégories:", error);
      
      // Solution alternative pour le développement - liste manuelle de quelques catégories
      const sampleCategories = [
        'africaCultural', 'afrobeatsModern', 'americaLatina',
        'anatomie', 'animalCrossing', 'animals', 'antiquity',
        'aot', 'archipelIsland', 'arsenalFc', 'artisticMovement',
        'asiaticCultural', 'assassinsCreed', 'astronomie', 'athletism',
        // 'attackontitan' supprimé car c'est un doublon avec 'aot'
        'attentatsAndCrises', 'batman', 'bigRealisator',
        'parisSaintGermain', 'peakyBlinders', 'peintres', 'peterPan',
        'plants', 'poetes', 'pop', 'power', 'preHistory', 'premierLeague',
        'prisonBreak', 'programmation', 'psychoPass',
        // 'quizzerverse' supprimé car demandé
        'rapFrModern', 'rapUsModern', 'reggae', 'retourVersLeFutur',
        'revolutionAndEmpire', 'rnb', 'rock', 'rollandGarros',
        'rugby', 'savoirFaireTraditional', 'scientificDiscovery', 'series'
      ];
      
      const imageArray = sampleCategories.map(cat => ({
        name: formatCategoryName(cat),
        originalName: cat,
        // Utilisez un chemin relatif pour les images
        src: `${process.env.PUBLIC_URL}/assets/categoriesImage/${cat}.jpg`
      }));
      
      // Triez les catégories par ordre alphabétique selon la langue actuelle
      imageArray.sort((a, b) => {
        const nameA = t(getStandardCategoryName(a.originalName)) || a.name;
        const nameB = t(getStandardCategoryName(b.originalName)) || b.name;
        return nameA.localeCompare(nameB);
      });
      
      setCategoryImages(imageArray);
      setLoaded(true);
    }
  }, [t]); // Ajouter t comme dépendance pour réagir aux changements de langue

  // Animation de défilement infini avec pause au survol
  useEffect(() => {
    if (!loaded || !containerRef.current || !carouselRef.current) return;

    // Dupliquer les éléments pour créer un défilement infini
    const carousel = carouselRef.current;
    const clone = carousel.cloneNode(true);
    containerRef.current.appendChild(clone);

    let animationFrameId;

    const animate = () => {
      if (!isPaused) {
        // Augmenter la position de défilement
        scrollPosRef.current += speedRef.current;
        
        // Réinitialiser la position une fois qu'une copie complète a défilé
        if (scrollPosRef.current >= carousel.offsetWidth) {
          scrollPosRef.current = 0;
        }
        
        // Appliquer la transformation
        containerRef.current.style.transform = `translateX(-${scrollPosRef.current}px)`;
      }
      
      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    // Nettoyage à la démonte du composant
    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [loaded, isPaused]);

  const handleCategoryHover = (index) => {
    // Mettre en pause le défilement
    setIsPaused(true);
    setActiveCategoryIndex(index);
    
    // Empêcher les événements de clic de se propager aux clones du carrousel
    const allItems = document.querySelectorAll('.category-item');
    allItems.forEach(item => {
      item.style.pointerEvents = 'none';
    });
    
    // Réactiver seulement l'élément survolé
    const activeItems = document.querySelectorAll(`.category-item:nth-child(${index + 1})`);
    activeItems.forEach(item => {
      item.style.pointerEvents = 'auto';
    });
  };

  const handleCategoryLeave = () => {
    // Reprendre le défilement à partir de la dernière position
    setIsPaused(false);
    setActiveCategoryIndex(null);
    
    // Réactiver tous les événements après avoir quitté
    const allItems = document.querySelectorAll('.category-item');
    allItems.forEach(item => {
      item.style.pointerEvents = 'auto';
    });
  };

  const getCounterStyle = () => {
    const style = {
      opacity: counterVisible ? 1 : 0,
      transform: `scale(${counterVisible ? 1 : 0.5})`,
      transition: 'opacity 0.5s, transform 0.5s, color 0.3s',
      color: '#FE02C9',
      pointerEvents: 'none' // Désactiver les clics
    };
    
    return style;
  };

  return (
    <section className="categories-section" id="categories">
      <CategoriesParticles />
      <div className="container">
        <h2 className="section-title">
          <span 
            className="counter-animation" 
            style={getCounterStyle()}
          >
            +{counterValue}
          </span>{" "}
          {t('themes')}
        </h2>
        
        <div className="categories-overview">
          <div className="categories-carousel-container">
            <div className="categories-carousel-inner" ref={containerRef}>
              <div className="categories-carousel" ref={carouselRef}>
                {categoryImages.map((category, index) => (
                  <CategoryItem 
                    key={index}
                    category={category}
                    index={index}
                    isActive={activeCategoryIndex === index}
                    onHover={handleCategoryHover}
                    offHover={handleCategoryLeave}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Categories; 